import React, { useCallback, useEffect, useState } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import { useFieldDefinitions } from '../contexts/FieldDefinitionsContext';
import { FilterCondition, FilterConditionRow } from './FilterConditionRow';
import searchService from '../services/search.service';
import {
  buildFilterJson,
  parseFilterJson,
} from '../pages/order-management/components/CustomFilterDialog';

/**
 * The in-place filter for a SYSTEM bucket card — the port of V1's "Custom Filter"
 * dialog, which filtered a search-results bucket where it stood instead of making you
 * build a copy of it.
 *
 * Deliberately NOT FirstCardModal. That modal edits a card: name, stat button, shared
 * flag, scope toggle, delete. None of those apply here — a system bucket's query is
 * canonical and its card is not the user's to rename or remove. All this collects is
 * conditions to AND onto the bucket's own WHERE for as long as the page stays open.
 *
 * Saved filters are the SAME pool the header magnifier's Custom Filter uses —
 * fastcms_db.quote_custom_filters under scope='orders' (the table is scope-generic
 * despite its quote-flavoured name), holding the identical FilterCondition[] JSON. That
 * is deliberate and is the whole point: a shape worth saving is worth reaching from
 * wherever you are, so one saved from a bucket shows up in the header search and vice
 * versa, rather than stranding people with two private lists that drift.
 *
 * Two properties the host guarantees and this dialog states plainly, because together
 * they are what makes filtering a shared bucket safe (see the footer note):
 *   * the filter is ephemeral — it survives refreshes and layout saves, and is gone
 *     once the user leaves the page, so a bucket can never be quietly narrowed for
 *     tomorrow's session; and
 *   * the panel counts ignore it — they stay on the global header filters alone, so
 *     everyone on the floor keeps reading the same numbers.
 */

/** Operators the backend acts on with no value typed (app/utils/dynamic_query_builder). */
const VALUELESS_OPERATORS = new Set(['Is empty', 'Is not empty']);
/** ...plus these two, which it REWRITES to the pair above when the value is blank. */
const BLANK_MEANS_EMPTY = new Set(['Equal to', 'Not equal to']);

/**
 * The rows the query will actually act on.
 *
 * `_build_where_conditions` silently skips a condition that has no field, no operator,
 * or no value on an operator that needs one — so a half-built row narrows nothing.
 * Applying the same rule here keeps the card's filter badge honest: it counts the
 * conditions that are really constraining the bucket, not the empty row at the bottom
 * of the builder.
 */
export function usableConditions(conditions: FilterCondition[]): FilterCondition[] {
  return conditions.filter((c) => {
    if (!c.field || !c.compare) return false;
    if (c.values.trim() !== '') return true;
    return VALUELESS_OPERATORS.has(c.compare) || BLANK_MEANS_EMPTY.has(c.compare);
  });
}

interface SavedFilter {
  recno: number;
  name: string;
  filter: string;
}

/** Canonical JSON, so a filter round-tripped through the builder compares equal to the
 *  stored one — that comparison is what drives the "edited" marker. */
function canon(raw?: string): string {
  return buildFilterJson(parseFilterJson(raw));
}

interface BucketFilterModalProps {
  isOpen: boolean;
  /** The bucket's display name, for the title — this dialog never renames anything. */
  bucketLabel: string;
  /** The filter currently applied to this bucket, so reopening resumes where it left off. */
  initialConditions?: FilterCondition[];
  /** Receives the usable conditions; an empty array means "clear this bucket's filter". */
  onApply: (conditions: FilterCondition[]) => void;
  onClose: () => void;
}

const EMPTY_ROW = (id: number): FilterCondition => ({ id, field: '', compare: '', values: '' });

const BucketFilterModal: React.FC<BucketFilterModalProps> = ({
  isOpen,
  bucketLabel,
  initialConditions,
  onApply,
  onClose,
}) => {
  const [conditions, setConditions] = useState<FilterCondition[]>([EMPTY_ROW(0)]);
  const [idCounter, setIdCounter] = useState(1);
  const { fieldDefinitions, loading: loadingFields } = useFieldDefinitions();

  const [saved, setSaved] = useState<SavedFilter[]>([]);
  const [saveName, setSaveName] = useState('');
  // The saved filter currently loaded, kept across edits so the header can show an
  // "edited, not saved" marker and offer to update it in place.
  const [loaded, setLoaded] = useState<{ name: string; filter: string } | null>(null);

  const refreshSaved = useCallback(
    () => searchService.listOrderCustomFilters().then(setSaved).catch(() => setSaved([])),
    [],
  );

  // Re-seed from props on open. The builder's rows are local state so typing doesn't
  // round-trip through the host on every keystroke, which means each open has to pull
  // the live filter back in — otherwise reopening a filtered bucket would show the
  // builder it had when it last closed, not what the grid is actually running.
  useEffect(() => {
    if (!isOpen) return;
    if (initialConditions && initialConditions.length > 0) {
      setConditions(initialConditions.map((c, i) => ({ ...c, id: i })));
      setIdCounter(initialConditions.length);
    } else {
      setConditions([EMPTY_ROW(0)]);
      setIdCounter(1);
    }
    setSaveName('');
    searchService.listOrderCustomFilters()
      .then((list) => {
        setSaved(list);
        // If what is already applied IS one of the saved filters, say so rather than
        // making the user wonder whether they are looking at theirs.
        const active = buildFilterJson(initialConditions || []);
        const hit = active ? list.find((f) => canon(f.filter) === active) : undefined;
        setLoaded(hit ? { name: hit.name, filter: canon(hit.filter) } : null);
      })
      .catch(() => setSaved([]));
  }, [isOpen, initialConditions]);

  const addCondition = useCallback(() => {
    setConditions((prev) => [...prev, EMPTY_ROW(idCounter)]);
    setIdCounter((prev) => prev + 1);
  }, [idCounter]);

  const removeCondition = useCallback((id: number) => {
    // Never drop to zero rows: an empty builder has no affordance to start from.
    setConditions((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : prev));
  }, []);

  const updateCondition = useCallback(
    (id: number, field: keyof FilterCondition, value: string) => {
      setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
    },
    [],
  );

  // Clear is an APPLY of nothing, not just a form reset: the point of the button is to
  // put the full bucket back on screen, which takes a refetch the host has to run.
  const clearAll = useCallback(() => {
    setConditions([EMPTY_ROW(idCounter)]);
    setIdCounter((prev) => prev + 1);
    onApply([]);
  }, [idCounter, onApply]);

  const apply = useCallback(() => onApply(usableConditions(conditions)), [conditions, onApply]);

  const currentJson = buildFilterJson(conditions);
  // The loaded filter has been edited since it was loaded — shown, not blocked: applying
  // an edited filter without saving it is a perfectly normal thing to want.
  const dirty = !!loaded && currentJson !== loaded.filter;

  const loadSaved = useCallback((recno: string) => {
    const f = saved.find((x) => String(x.recno) === recno);
    if (!f) return;
    const parsed = parseFilterJson(f.filter);
    setConditions(parsed.length ? parsed : [EMPTY_ROW(0)]);
    setIdCounter(parsed.length || 1);
    setLoaded({ name: f.name, filter: canon(f.filter) });
  }, [saved]);

  const saveAs = useCallback(async () => {
    const name = saveName.trim();
    if (!name || !currentJson) return;
    await searchService.saveOrderCustomFilter(name, currentJson);
    setLoaded({ name, filter: currentJson });
    setSaveName('');
    refreshSaved();
  }, [saveName, currentJson, refreshSaved]);

  const updateLoaded = useCallback(async () => {
    if (!loaded || !currentJson) return;
    await searchService.saveOrderCustomFilter(loaded.name, currentJson);
    setLoaded({ name: loaded.name, filter: currentJson });
    refreshSaved();
  }, [loaded, currentJson, refreshSaved]);

  // Alt+C closes, matching every other modal on these dashboards.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const drag = useModalDrag(isOpen);

  if (!isOpen) return null;

  const activeCount = usableConditions(conditions).length;

  return (
    <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div
        ref={drag.panelRef}
        style={drag.panelStyle}
        className="bg-panel rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
      >
        <ModalHeader
          title={`Filter: ${bucketLabel}`}
          onClose={onClose}
          dragHandleProps={drag.dragHandleProps}
        />

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="block text-sm font-medium text-label">
                Filter Conditions
                <span className="ml-2 font-normal text-muted">
                  applied on top of this bucket&rsquo;s own criteria
                </span>
              </label>
              <div className="flex items-center gap-2">
                {loaded && (
                  <span
                    title={dirty ? 'Edited — not yet saved' : 'Saved filter'}
                    className={`text-xs truncate ${dirty
                      ? 'italic text-amber-600 dark:text-amber-400'
                      : 'font-medium text-indigo-700 dark:text-indigo-300'}`}
                  >
                    {loaded.name}{dirty ? ' *' : ''}
                  </span>
                )}
                {saved.length > 0 && (
                  <select
                    aria-label="Load saved filter"
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
                    value=""
                    onChange={(e) => loadSaved(e.target.value)}
                  >
                    <option value="">Load saved filter…</option>
                    {saved.map((f) => (
                      <option key={f.recno} value={f.recno}>{f.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {conditions.map((condition, index) => (
              <FilterConditionRow
                key={condition.id}
                condition={condition}
                fieldDefinitions={fieldDefinitions}
                loadingFields={loadingFields}
                onUpdate={updateCondition}
                onRemove={removeCondition}
                removeDisabled={conditions.length === 1}
                autoFocus={index === 0 && !condition.field}
                // Tabbing off the last row adds the next one, so a multi-condition
                // filter can be built without going back to the mouse.
                onTabOut={index === conditions.length - 1 ? addCondition : undefined}
              />
            ))}

            <button
              onClick={addCondition}
              type="button"
              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Filter
            </button>
          </div>

          <p className="mt-4 text-xs text-muted">
            This filter applies to the rows in this bucket only. The bucket counts on the
            dashboard keep following the header filters alone, so they stay the same for
            everyone. The filter clears when you leave the page — save it a name below to
            reuse it later, here or anywhere else that filters orders.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-rule px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={clearAll}
              disabled={activeCount === 0}
              className="px-3 py-2 text-sm font-medium text-label underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
            >
              Clear Filter
            </button>
            {/* Save As / Update. Writes to the same saved-filter pool the header's
                Custom Filter reads, so a shape built here is reusable from anywhere
                that filters orders — the reason to save one at all. */}
            {dirty && (
              <button
                type="button"
                onClick={updateLoaded}
                title={`Overwrite the saved filter "${loaded?.name}"`}
                className="px-2 py-1 text-xs font-semibold rounded border border-indigo-400 text-indigo-700 dark:border-indigo-500 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
              >
                Update &ldquo;{loaded?.name}&rdquo;
              </button>
            )}
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveAs(); } }}
              placeholder="Save as…"
              aria-label="Name for a new saved filter"
              className="w-32 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
            />
            <button
              type="button"
              onClick={saveAs}
              disabled={!saveName.trim() || activeCount === 0}
              className="px-2 py-1 text-xs font-semibold rounded border border-gray-300 dark:border-gray-600 text-label hover:bg-highlight disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply{activeCount > 0 ? ` (${activeCount})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BucketFilterModal;
